/**
 * ClassificationPreview Component (CCE-005)
 * 
 * Displays and allows editing of 9-facet content classification results.
 * 
 * Features:
 * - 3-column layout (ContentApprovalReview pattern - AC #13)
 * - All 9 facets with confidence badges
 * - Manual override dropdowns
 * - "Needs Review" alert banner (AC #4)
 * - Loading skeleton (AC #10)
 * - Error state with retry (AC #11)
 * - EntitySelector integration (AC #12)
 * - Responsive design (AC #7)
 * - WCAG AA accessible (AC #8)
 * 
 * Created: February 3, 2026
 * Epic: CCE - Content Classification Enhancement
 */

import React, { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import {
  ContentClassificationResult,
  getFacetValues,
  getFacetLabel,
  EntityType,
} from '../../types/content-classification';
import { FacetRow } from './FacetRow';
import { ConfidenceBadge } from './ConfidenceBadge';

export interface ClassificationPreviewProps {
  classification: ContentClassificationResult | null;
  onClassificationChange?: (updated: ContentClassificationResult) => void;
  readOnly?: boolean;
  showConfidence?: boolean;
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

/**
 * Skeleton loader for classification preview
 */
const SkeletonLoader: React.FC<{ rows: number }> = ({ rows }) => (
  <div className="space-y-3 animate-pulse">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center justify-between py-3 px-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="h-6 bg-gray-200 rounded w-20"></div>
      </div>
    ))}
  </div>
);

export const ClassificationPreview: React.FC<ClassificationPreviewProps> = ({
  classification,
  onClassificationChange,
  readOnly = false,
  showConfidence = true,
  loading = false,
  error = null,
  onRetry,
}) => {
  const [showEntitySelector, setShowEntitySelector] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{
    id: string;
    type: string;
    name: string;
  } | null>(null);

  // Loading state (AC #10)
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-4">
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
        </div>
        <SkeletonLoader rows={9} />
      </div>
    );
  }

  // Error state (AC #11)
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-red-800">
              Classification Failed
            </h3>
            <p className="mt-2 text-sm text-red-700">
              {error.message || 'An unexpected error occurred while classifying content.'}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Classification
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No classification data
  if (!classification) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-sm text-gray-500">
          No classification data available. Upload and classify a document to see results.
        </p>
      </div>
    );
  }

  /**
   * Handle manual override of facet value (AC #6)
   */
  const handleOverride = (facetName: string, newValue: string) => {
    if (!onClassificationChange) return;

    const updated: ContentClassificationResult = {
      ...classification,
      [facetName]: newValue,
      [`${facetName}_confidence`]: 1.0, // Manual override = 100% confidence
      requires_human_review: false, // Clear review flag on manual fix
      review_reasons: classification.review_reasons.filter(
        (r) => !r.toLowerCase().includes(facetName.toLowerCase())
      ),
    };

    onClassificationChange(updated);
  };

  // 3-column layout per ContentApprovalReview pattern (AC #13)
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Left 2 columns: Facets display/edit */}
      <div className="col-span-2">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Content Classification
            </h3>
            {showConfidence && (
              <p className="mt-1 text-sm text-gray-500">
                Overall Confidence: {Math.round(classification.overall_confidence * 100)}%
              </p>
            )}
          </div>

          {/* "Needs Review" alert banner (AC #4) */}
          {classification.requires_human_review && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Manual Review Required
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p className="mb-2">
                      This classification has low confidence in one or more facets.
                      Please review and correct if necessary.
                    </p>
                    {classification.review_reasons.length > 0 && (
                      <ul className="list-disc pl-5 space-y-1">
                        {classification.review_reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Facets display (AC #2, #5) */}
          <div className="divide-y divide-gray-100">
            <FacetRow
              facetName="Format Type"
              facetValue={classification.format_type}
              confidence={classification.format_confidence}
              possibleValues={getFacetValues('format_type')}
              onOverride={(v) => handleOverride('format_type', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Purpose"
              facetValue={classification.purpose_type}
              confidence={classification.purpose_confidence}
              possibleValues={getFacetValues('purpose_type')}
              onOverride={(v) => handleOverride('purpose_type', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Primary Audience Level"
              facetValue={classification.primary_audience_level}
              confidence={classification.audience_level_confidence}
              possibleValues={getFacetValues('primary_audience_level')}
              onOverride={(v) => handleOverride('primary_audience_level', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Audience Domain"
              facetValue={classification.audience_domain}
              confidence={classification.audience_domain_confidence}
              possibleValues={getFacetValues('audience_domain')}
              onOverride={(v) => handleOverride('audience_domain', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Industry"
              facetValue={classification.industry_type}
              confidence={classification.industry_confidence}
              possibleValues={getFacetValues('industry_type')}
              onOverride={(v) => handleOverride('industry_type', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Buyer Stage"
              facetValue={classification.buyer_stage}
              confidence={classification.buyer_stage_confidence}
              possibleValues={[
                { value: 'awareness', label: 'Awareness' },
                { value: 'consideration', label: 'Consideration' },
                { value: 'evaluation', label: 'Evaluation' },
                { value: 'decision', label: 'Decision' },
                { value: 'purchase', label: 'Purchase' },
                { value: 'onboarding', label: 'Onboarding' },
                { value: 'adoption', label: 'Adoption' },
                { value: 'advocacy', label: 'Advocacy' },
                { value: 'retention', label: 'Retention' },
              ]}
              onOverride={(v) => handleOverride('buyer_stage', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Technical Depth"
              facetValue={classification.technical_depth}
              confidence={classification.technical_depth_confidence}
              possibleValues={getFacetValues('technical_depth')}
              onOverride={(v) => handleOverride('technical_depth', v)}
              readOnly={readOnly}
            />

            <FacetRow
              facetName="Subject Discipline"
              facetValue={classification.subject_discipline}
              confidence={classification.discipline_confidence}
              possibleValues={getFacetValues('subject_discipline')}
              onOverride={(v) => handleOverride('subject_discipline', v)}
              readOnly={readOnly}
            />

            {/* Entity Attribution with EntitySelector integration (AC #12) */}
            <div className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors">
              <div className="flex-1 min-w-0">
                <label className="text-sm font-medium text-gray-700">
                  Attributed Entity
                </label>
              </div>
              <div className="flex-1 flex justify-center min-w-0 mx-4">
                <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                  {getFacetLabel(classification.entity_type)}
                  {classification.entity_id && ` (ID: ${classification.entity_id.slice(0, 8)}...)`}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <ConfidenceBadge confidence={classification.entity_confidence} size="sm" />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEntity({
                        id: classification.entity_id || '',
                        type: classification.entity_type || 'client',
                        name: getFacetLabel(classification.entity_type || 'client'),
                      });
                      setShowEntitySelector(true);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Change Entity
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right 1 column: Review panel */}
      <div className="col-span-1">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Review Status
          </h3>

          {/* Overall confidence summary */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Overall</span>
              <ConfidenceBadge confidence={classification.overall_confidence} />
            </div>
            {classification.requires_human_review && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Review Required
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {classification.review_reasons.length} issue(s) found
                </p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="border-t border-gray-200 pt-4 space-y-3">
            <div>
              <span className="text-xs text-gray-500">Visibility</span>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {getFacetLabel(classification.visibility)}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Time Sensitivity</span>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {getFacetLabel(classification.time_sensitivity)}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Version</span>
              <p className="text-sm font-medium text-gray-900 mt-1">
                v{classification.version}
                {classification.is_latest && (
                  <span className="ml-2 text-xs text-green-600">Latest</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Entity Attribution Override Modal (CCE-015 AC #2, #3, #4) */}
      {showEntitySelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Override Entity Attribution
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Select the correct entity for this document. This will create an audit trail
              of the change.
            </p>
            
            {/* Simple entity selection for now - in future could integrate full EntitySelector */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entity Type
                </label>
                <select
                  value={selectedEntity?.type || 'ENT_CLIENT'}
                  onChange={(e) => setSelectedEntity(prev => prev ? { ...prev, type: e.target.value } : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="ENT_CLIENT">Client</option>
                  <option value="ENT_PARTNER">Partner</option>
                  <option value="ENT_VENDOR">Vendor</option>
                  <option value="ENT_SOLUTION">Solution</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entity ID
                </label>
                <input
                  type="text"
                  value={selectedEntity?.id || ''}
                  onChange={(e) => setSelectedEntity(prev => prev ? { ...prev, id: e.target.value } : null)}
                  placeholder="Enter entity UUID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowEntitySelector(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedEntity?.id}
                onClick={() => {
                  if (selectedEntity && onClassificationChange && classification) {
                    // Update classification with new entity
                    const updated: ContentClassificationResult = {
                      ...classification,
                      entity_id: selectedEntity.id,
                      entity_type: selectedEntity.type as EntityType,
                      entity_confidence: 1.0, // Manual override
                      requires_human_review: false,
                    };
                    onClassificationChange(updated);
                  }
                  setShowEntitySelector(false);
                }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassificationPreview;
